// Video model versions the Create surfaces can generate with, and everything that differs
// between them. One module so the video tab, the Animate modal, the pose picker, the submit
// path and the Clone/Extend actions can never drift apart on what a version supports.
//
// Mirrors the backend contract in `myapp/video3_service.py` (SUPPORTED_VIDEO_VERSIONS,
// LTX_ASPECTS, LTX_GEM_COST) and `var/video3/ltx_loras_data.py` (DIALOGUE_*, CAMERA_LORAS,
// CUSTOM_VIDEO_POSES). If a constant here disagrees with the backend the backend wins — it
// charges the gems and it rejects the request.

export const VIDEO_V5 = 'video5' // LTX 2.3 — one job, 704x1280 @ 24fps, native audio
export const VIDEO_V4 = 'video4' // WAN 2.2 — the previous default, still fully supported

export const isLtx = (version) => version === VIDEO_V5

// ------------------------------------------------------------------------------ model selection
//
// The user does not pick a MODEL NUMBER, they pick an OUTCOME. "v4 vs v5" asked them to know that
// v5 means LTX, that LTX is the only model that can speak, and that it trades motion quality to
// do it — three facts they have no way to learn from two version numbers.
//
// So the switcher is Auto / v4 / v4-voice, and Auto is the default:
//   * a spoken line REQUIRES the voice model — WAN cannot lip-sync at all, so a dialogue line on
//     v4 is silently dropped. Auto makes that impossible.
//   * everything else stays on v4, which is the stronger model for MOTION.
//
// Forcing remains available (an explicit choice always beats inference), and it is the only way
// to get a silent voice-model clip or a v4 clip while a line sits in the box.
export const VIDEO_MODEL_AUTO = 'auto'

/** The version a mode actually renders with. `auto` is the only value that depends on input:
 *  a non-empty dialogue line selects the voice model, everything else stays on v4. A forced
 *  mode is returned verbatim, including "voice model with no line". */
export function resolveVideoVersion(mode, dialogue) {
  if (mode === VIDEO_V4 || mode === VIDEO_V5) return mode
  return (dialogue || '').trim() ? VIDEO_V5 : VIDEO_V4
}

/** True when Auto is what put us on the voice model, i.e. the user typed a line rather than
 *  choosing the model. Lets the UI explain the switch instead of silently reshaping the form. */
export const isAutoVoice = (mode, dialogue) =>
  mode === VIDEO_MODEL_AUTO && resolveVideoVersion(mode, dialogue) === VIDEO_V5

// ------------------------------------------------------- how this compares to v4's output
//
// What v4 actually delivers today, end to end:
//   resolution  480x832 generated, x2 upscale on quality High  ->  960x1664
//   frame rate  16 fps generated, RIFE x2 ALWAYS               ->  32 fps
//
// The voice model's "high" tier is the SAME 960x1664, so High is the setting that matches v4's
// picture — but it is not the default. It measures ~104s of GPU against ~31s at Fast, and the
// voice model runs on a single pod, so defaulting to High would mean every second user waits
// out a 104s render. Fast ships; High is one tap away. Revisit when there is a second pod.
//
// Frame rate needs no equivalent choice: 24 is closer to v4's 32 than 48 is, 24 REAL frames
// beats v4's 16 real + 16 interpolated, and 48 runs the same RIFE pass that produced the
// measured frame-blending.

// ---------------------------------------------------------------------------- v5 capabilities

// Base price (6s), kept for callers that don't know the length. Longer clips cost more — see LTX_LENGTHS / ltxGemsForLength. Audio is
// included at every length: LTX generates its soundtrack in the same job, so there is no
// separate audio pass to charge for and no Standard/High tier to pick between.
export const LTX_GEM_COST = 18

// Selectable clip length. Mirrors the backend LTX_LENGTHS / LTX_GEM_COST whitelist — the backend
// owns both the frame count and the price, this is only the menu and the displayed cost.
//
// Prices come from WAN's real gems-per-GPU-second (0.565, from n=3851 jobs: 15.7 gems / 27.8s
// median generation GPU) applied to LTX's measured GPU time (30s / 51s / 82s) — so the two
// versions cost the same per unit of GPU. See LTX_GEM_COST in myapp/video3_service.py for the
// full derivation.
//
// 15s and 20s are NOT offered. Both render fine (the node cap is 16384 frames) but both drift
// measurably (-8.9 / +17 Y start-to-end) and read as glitchy rather than simply longer, so the
// ceiling is a quality decision. 15s shipped briefly and was pulled for exactly that.
//
// NOTE: this is the length of ONE clip and is NOT a replacement for extend. Extend will chain
// clips; when it lands it should read the source's length so a continuation matches it.
export const LTX_LENGTHS = [
  { value: 6, gems: 18 },
  { value: 10, gems: 30 },
]
export const LTX_LENGTH_DEFAULT = 6

// Optional 2x frame interpolation to 48fps, run AFTER generation on the existing RIFE interframe
// pods — the render itself is identical either way. Surcharge is keyed by clip length because
// the interpolation pass scales with the clip (measured 9.0s GPU at 6s, 13.4s at 10s).
//
// Opt-in on purpose: measured clean on a talking/dancing clip, but NOT tested on fast motion,
// which is where RIFE smears worst.
//
// The interframe pods are $0.69/hr against the LTX pod's $1.99/hr, so in DOLLARS interpolation
// is only ~10% of the generation it is attached to — the surcharge is set just above that, not
// at WAN's gems-per-second (which assumed identical hardware and came out ~4x too high).
// See LTX_FPS_GEM_COST in myapp/video3_service.py for the full derivation.
export const LTX_FPS_OPTIONS = [24, 48]
export const LTX_FPS_DEFAULT = 24
const LTX_FPS_SURCHARGE = { 6: 3, 10: 5 }

// Render resolution. "standard" is the shipped 704x1280; "high" renders 960x1664, which is the
// resolution the LTX community converges on. Measured at ~53s vs ~31s on the same graph (+73%
// GPU), hence the surcharge.
//
// Opt-in, and standard stays the default: side-by-side testing did not find an improvement in
// FACIAL detail specifically — LTX generates a 352x640 latent either way and regenerates the
// face from it — so this buys a larger, cleaner render rather than a better face.
// Mirrors LTX_RESOLUTIONS in myapp/video3.py.
export const LTX_RESOLUTIONS = [
  // `value` is the backend contract and must stay "standard"/"high"; the label is display only.
  { value: 'standard', labelKey: 'resolutionFast', dims: '704×1280' },
  { value: 'high', labelKey: 'resolutionHigh', dims: '960×1664' },
]
export const LTX_RESOLUTION_DEFAULT = 'standard'
const LTX_RES_SURCHARGE = { 6: 13, 10: 22 }

/** Fold an unknown/absent resolution onto the default, matching the backend's fallback. */
export const normalizeResolution = (value) =>
  LTX_RESOLUTIONS.some((r) => r.value === value)
    ? value
    : LTX_RESOLUTION_DEFAULT

/** Total gems for a v5 clip: base price for the length, plus the interpolation surcharge when
 *  48fps is selected and the resolution surcharge when High is. Mirrors LTX_GEM_COST +
 *  LTX_FPS_GEM_COST + LTX_RES_GEM_COST in myapp/video3_service.py. */
export function ltxGemsFor(seconds, fps, resolution = LTX_RESOLUTION_DEFAULT) {
  const len =
    LTX_LENGTHS.find((l) => l.value === Number(seconds)) ||
    LTX_LENGTHS.find((l) => l.value === LTX_LENGTH_DEFAULT)
  const extra = Number(fps) === 48 ? LTX_FPS_SURCHARGE[len.value] || 0 : 0
  const res =
    normalizeResolution(resolution) === 'high'
      ? LTX_RES_SURCHARGE[len.value] || 0
      : 0
  return len.gems + extra + res
}

/** Extra gems the High resolution adds for this clip length, 0 at Standard. Lets the resolution
 *  section show its own cost without the clip length section double-counting it. */
export function ltxResolutionSurcharge(seconds, resolution) {
  return (
    ltxGemsFor(seconds, LTX_FPS_DEFAULT, resolution) - ltxGemsForLength(seconds)
  )
}

/** Gems for a v5 clip of this length. Unknown/absent falls back to the default length's price,
 *  which is what the backend charges for an unrecognised value. */
export function ltxGemsForLength(seconds) {
  return ltxGemsFor(seconds, LTX_FPS_DEFAULT)
}

/** Extra gems the 48fps pass adds for this clip length, 0 at the native rate. Lets the frame
 *  rate section show its own cost without the clip length section double-counting it. */
export function ltxFpsSurcharge(seconds, fps) {
  return ltxGemsFor(seconds, fps) - ltxGemsForLength(seconds)
}

// The workflow was tuned portrait/landscape only — a Square submit is a 400 before any gems are
// charged, so the UI must never offer it.
export const LTX_ASPECTS = ['Portrait', 'Wide']

// A ~6s clip (145 frames at 24fps) fits roughly 14 words before LTX starts dropping them off
// both ends. The backend CLAMPS rather than rejects, so an over-long line still returns a video —
// the counter exists so that trimming is never a surprise. The char cap is a backstop against one
// absurd token, NOT the primary limit: a measured 22-word failure was only 97 chars.
//
// Words are a DURATION budget, so the cap is keyed by clip length: picking 10s buys more speech,
// not just more video. Mirrors DIALOGUE_MAX_WORDS / DIALOGUE_MAX_CHARS in
// `var/video3/ltx_loras_data.py` — the backend owns the real clamp, this is the live counter.
//
// PROVISIONAL: 14 @ 6s is measured; 23 @ 10s is the same ~2.3 words/sec extended arithmetically
// and is not render-tested. Change it on the backend first, then here.
const LTX_DIALOGUE_MAX_WORDS = { 6: 14, 10: 23 }
const LTX_DIALOGUE_MAX_CHARS = { 6: 120, 10: 200 }

/** `{ words, chars }` for a clip length. Unknown/absent falls back to the shortest clip's caps,
 *  matching `ltx_loras_data.dialogue_limits` — the counter must never promise more words than
 *  the backend will actually speak. */
export function ltxDialogueLimits(seconds) {
  const len = Number(seconds)
  return {
    words:
      LTX_DIALOGUE_MAX_WORDS[len] ?? LTX_DIALOGUE_MAX_WORDS[LTX_LENGTH_DEFAULT],
    chars:
      LTX_DIALOGUE_MAX_CHARS[len] ?? LTX_DIALOGUE_MAX_CHARS[LTX_LENGTH_DEFAULT],
  }
}

// Sent verbatim — the backend whitelists these two strings and 400s anything else.
export const LTX_DIALOGUE_SPEAKERS = ['woman', 'man']
export const LTX_DIALOGUE_SPEAKER_DEFAULT = 'woman'

// Tone of voice for a spoken line. Mirrors the backend DIALOGUE_TONES whitelist; the backend
// owns the actual prompt wording, this is only the menu.
//
// Grounded in Lightricks' LTX-2 guidance plus the community guides: the model responds to MANNER
// KEYWORDS ("whispers", "purrs", "chirps"), ACOUSTIC description, and ARCHETYPES ("energetic
// announcer") — and NOT to emotion labels. So these are labels for a DELIVERY, not a feeling.
//
// FOUR, not seven. Cute/Cheerful/Anime all sat in the same bright-high-fast corner and Shy sat on
// top of Whisper, so the presets were genuinely different but not audibly so. These three occupy
// separated corners of pitch x volume x pace.
//
// 'whisper' is honest-but-limited: the base model does not do a true devoiced whisper (that is
// what the LTX-2.3 Whisper/Soft-Spoken audio LoRA exists for), so it reads as soft and breathy
// until that LoRA is baked into the pod image.
export const LTX_DIALOGUE_TONES = [
  { value: 'neutral', labelKey: 'toneNeutral' },
  { value: 'seductive', labelKey: 'toneSeductive' },
  { value: 'whisper', labelKey: 'toneWhisper' },
  { value: 'playful', labelKey: 'tonePlayful' },
]
export const LTX_DIALOGUE_TONE_DEFAULT = 'neutral'

/** Retired presets, folded to the survivor in the same acoustic corner. These values are sitting
 *  in real users' localStorage — without this the <select> would render blank for them, since a
 *  value with no matching <option> shows as empty. The backend folds the same way. */
const LTX_TONE_ALIASES = {
  cute: 'playful',
  cheerful: 'playful',
  anime: 'playful',
  shy: 'whisper',
}
export const normalizeTone = (tone) => {
  if (LTX_DIALOGUE_TONES.some((t) => t.value === tone)) return tone
  return LTX_TONE_ALIASES[tone] || LTX_DIALOGUE_TONE_DEFAULT
}

// Accent for a spoken line. Mirrors the backend DIALOGUE_ACCENTS whitelist; the backend owns the
// wording, this is only the menu. Started as a two-way American/British experiment; confirmed
// audible, so widened — then trimmed back to the five that hold up by ear. Irish, Italian,
// German, Russian and Japanese rendered weakly or not at all, and a menu entry that does nothing
// is worse than no entry.
//
// 'default' rather than 'none': there is no such thing as speaking with no accent — the model
// always picks one, this option just doesn't steer which.
export const LTX_DIALOGUE_ACCENTS = [
  { value: 'default', labelKey: 'accentDefault' },
  { value: 'american', labelKey: 'accentAmerican' },
  { value: 'british', labelKey: 'accentBritish' },
  { value: 'australian', labelKey: 'accentAustralian' },
  { value: 'latin', labelKey: 'accentLatin' },
  { value: 'french', labelKey: 'accentFrench' },
]
export const LTX_DIALOGUE_ACCENT_DEFAULT = 'default'

/** 'none' was the original key — persisted in browsers and written onto every video document
 *  rendered so far, so it is folded, not dropped. The backend folds the same way. */
const LTX_ACCENT_ALIASES = {
  none: 'default',
  // Briefly shipped, then dropped. Videos carrying them exist, so a clone folds instead of
  // rendering the select blank.
  irish: 'default',
  italian: 'default',
  german: 'default',
  russian: 'default',
  japanese: 'default',
}
export const normalizeAccent = (accent) => {
  if (LTX_DIALOGUE_ACCENTS.some((a) => a.value === accent)) return accent
  return LTX_ACCENT_ALIASES[accent] || LTX_DIALOGUE_ACCENT_DEFAULT
}

/** i18n key for a speaker value. `woman`/`man` already exist as generic keys in some locales
 *  with unrelated meanings, so these get their own namespace. */
export const speakerLabelKey = (speaker) =>
  speaker === 'man' ? 'speakerMan' : 'speakerWoman'

// Camera motion. The empty value is the default and adds nothing to the prompt — no camera lora
// and no trigger phrase, so the payload is byte-identical to a request that never sent the field.
// It is labelled "None" rather than "Auto": users read "Auto" as "the model picks a move for you"
// and asked for a way to turn that off, when this option already IS that. (The `cameraAuto` i18n
// key is kept under its old name — renaming it buys nothing and touches every locale file.)
// It does NOT lock the camera off; nothing suppresses the model's own framing, which is what
// `cameraMotionHint` says. A real static-camera option needs a CAMERA_LORAS entry backend-side.
//
// This list intentionally mirrors ONLY what the live pod image can serve. Camera LoRAs are baked
// into the pod image, so adding one is an image rebuild, not a data change — when the backend
// bakes `LTX2.3_CameraControls` and adds its tilt/pan/zoom-in entries to CAMERA_LORAS, add the
// matching options here.
export const LTX_CAMERA_MOVES = [
  { value: '', labelKey: 'cameraAuto' },
  { value: 'zoom_out', labelKey: 'cameraZoomOut' },
  // Handheld steers through the prompt alone — there is no handheld lora on the pod image, and a
  // phrase ships today where a lora is an image rebuild. It therefore costs no pose slot.
  { value: 'handheld', labelKey: 'cameraHandheld' },
]

// NOTE: poses are NOT a v5 concept. Every video pose comes from the source image's pose, exactly
// as it does on v4 — including the ones that carry an LTX motion lora (Group Dance, Blowjob With
// Hands, Pussy Job, Belly Bulge), which are ordinary entries in the image pose list. There is a
// legacy `ltx_pose` request field for the days when those four had no image equivalent; nothing
// sends it any more.

// Extend is supported on EVERY video version. v5 gained its own extend pipeline (own workflow,
// pose table via `ltx_lora`, and a join post-process) so the old "v4 only" gate is gone — the
// backend branches internally on the source's version.
//
// Kept as a function rather than deleted: the call sites read as a capability question, and the
// real remaining limits are per-request, not per-version (v5 extend 400s a Square source, and
// MAX_EXTENDS caps depth with a 409). Those are surfaced by the submit response, not here.
export const supportsExtend = () => true

// ------------------------------------------------------------------------------------ helpers

/** Which version generated a saved video doc. Docs written before v5 carry only the numeric
 *  `version` (1, 2, 3.1, 4); v5 docs also carry the `video_version` string. Anything that isn't
 *  recognisably v5 clones as v4 — the only other version the submit endpoint accepts. */
export function videoVersionFromDoc(doc) {
  if (!doc) return VIDEO_V4
  if (doc.video_version === VIDEO_V5 || doc.videoVersion === VIDEO_V5)
    return VIDEO_V5
  const numeric = Number(doc.version ?? doc.videoVersion)
  return numeric === 5 ? VIDEO_V5 : VIDEO_V4
}

// ------------------------------------------------------------------ carrying settings around
//
// Every feed builds its card objects as an explicit WHITELIST of document fields — Explore,
// Profile, Search and the image detail page each keep their own. A voice setting missing from one
// of them is `undefined` at the Clone call site, and Create's clone reader mostly guards on
// presence, so the setting is silently dropped rather than erroring: a High-resolution clip comes
// back at Fast, an accent reverts to Default, a spoken line disappears entirely. Four lists drifted
// exactly that way, so the shape lives here once instead of being retyped per surface.

/** The voice-model settings a saved video document carries, in document (snake_case) form. */
export function videoSettingsFromDoc(data) {
  return {
    video_version: data?.video_version ?? null,
    camera_move: data?.camera_move ?? null,
    dialogue: data?.dialogue ?? null,
    dialogue_speaker: data?.dialogue_speaker ?? null,
    dialogue_tone: data?.dialogue_tone ?? null,
    dialogue_accent: data?.dialogue_accent ?? null,
    video_length: data?.video_length ?? null,
    video_fps: data?.video_fps ?? null,
    video_resolution: data?.video_resolution ?? null,
  }
}

/** The same settings in the shape `cloneData` uses, so a cloned clip reproduces the one it came
 *  from. `videoVersion` is the STRING the submit endpoint takes — a bare numeric `version` is not
 *  a valid `video_version`. */
export function videoSettingsToClone(imageData) {
  return {
    videoVersion: videoVersionFromDoc(imageData),
    cameraMove: imageData?.camera_move ?? '',
    dialogue: imageData?.dialogue ?? '',
    dialogueSpeaker: imageData?.dialogue_speaker ?? null,
    dialogueTone: imageData?.dialogue_tone ?? null,
    dialogueAccent: imageData?.dialogue_accent ?? null,
    videoLength: imageData?.video_length ?? null,
    videoFps: imageData?.video_fps ?? null,
    videoResolution: imageData?.video_resolution ?? null,
  }
}

/** Display name for a saved video's numeric `version`. The voice model writes 5, and it is
 *  labelled with the name the UI uses for it everywhere else — "v5" appears nowhere a user can
 *  see any more, so a badge reading "v5" would name a model that does not exist in the product.
 *
 *  Unknown/absent falls through to v4: Explore and Profile map a missing `version` to 1, so 1 is
 *  not distinguishable from "predates versioning" and has always been shown as v4. */
export function videoVersionLabel(version) {
  const n = Number(version)
  if (n === 5) return 'v4-voice'
  if (n === 3.1) return 'v3.1'
  if (n === 3) return 'v3.0'
  if (n === 2) return 'v2.0'
  return 'v4.0'
}

/** True for a model still on offer, as opposed to a legacy tier. Drives the gold treatment on
 *  the card badge — the newest model must not read as more downlevel than the one beside it. */
export const isCurrentVideoVersion = (version) =>
  Number(version) === 4 || Number(version) === 5

/** An aspect this version can actually render. v5 has no Square, so a stored/inherited Square
 *  becomes Portrait instead of a 400 at submit. */
export function normalizeAspectForVersion(aspect, version) {
  if (!isLtx(version)) return aspect
  return LTX_ASPECTS.includes(aspect) ? aspect : 'Portrait'
}

/** Word count as the backend counts it — whitespace-split after the same normalisation
 *  `clamp_dialogue` applies, so the counter and the clamp agree on where the limit falls. */
export function dialogueWordCount(text) {
  if (!text) return 0
  return text.trim().split(/\s+/).filter(Boolean).length
}
